// src/pages/TheoDoiDonHangPage.js
import React, { useState, useEffect, useMemo } from 'react';
import { db } from '../firebase';
import { collection, onSnapshot, query, where, orderBy as fbOrderBy } from 'firebase/firestore';
import {
  Box, Typography, Paper, Table, TableContainer, TableHead, TableRow, TableCell,
  TableBody, TextField, TablePagination, Chip, Tooltip, Dialog, DialogContent, IconButton, Grid
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ReceiptIcon from '@mui/icons-material/Receipt';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import CancelIcon from '@mui/icons-material/Cancel';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { useAuth } from '../context/AuthContext';

// Helper Functions
const formatDateTime = (timestamp) => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp.seconds * 1000).toLocaleString('vi-VN');
};

const getOrderStatusChip = (status) => {
    switch (status) {
        case 'shipped': return <Chip label="Đã chuyển" color="success" size="small" />;
        case 'cancelled': return <Chip label="Đã hủy" color="error" size="small" />;
        case 'other': return <Chip label="Khác" color="default" size="small" />;
        case 'processing': default: return <Chip label="Đang xử lý" color="info" size="small" />;
    }
};

// StatCard component for displaying key metrics
const StatCard = ({ title, value, icon, color, onClick, selected }) => (
    <Paper 
        elevation={3} 
        sx={{ 
            p: 2, 
            display: 'flex', 
            alignItems: 'center', 
            height: '100%',
            cursor: 'pointer',
            border: selected ? `2px solid ${color || '#1976d2'}` : '2px solid transparent',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: 6,
            }
        }}
        onClick={onClick}
    >
        <Box sx={{ mr: 2, color: color || 'text.secondary' }}>{icon}</Box>
        <Box>
            <Typography variant="h5" component="div" sx={{ fontWeight: 'bold' }}>{value}</Typography>
            <Typography variant="body2" color="text.secondary">{title}</Typography>
        </Box>
    </Paper>
);

function TheoDoiDonHangPage() {
  const [exports, setExports] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [recoveries, setRecoveries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const { currentUser } = useAuth();
  const [statusFilter, setStatusFilter] = useState('all');
  
  // State cho trình xem ảnh
  const [imageViewer, setImageViewer] = useState({ open: false, images: [], currentIndex: 0 });

  useEffect(() => {
    const unsubCustomers = onSnapshot(collection(db, 'customers'), (snapshot) => {
        setCustomers(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });

    const q = query(collection(db, "exports"), fbOrderBy("exportDate", "desc"));
    const unsubExports = onSnapshot(q, (snapshot) => {
        setExports(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        setLoading(false);
    });

    const qRecoveries = query(collection(db, "recoveries"));
    const unsubRecoveries = onSnapshot(qRecoveries, (snapshot) => {
        setRecoveries(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });
    
    return () => {
      unsubCustomers();
      unsubExports();
      unsubRecoveries();
    };
  }, []);
  
  const { processedOrders, statusMetrics } = useMemo(() => {
    if (!currentUser) return { processedOrders: [], statusMetrics: {} };

    const customerMap = new Map(customers.map(c => [c.id, c]));
    
    // Tạo map để tra cứu mảng hình ảnh
    const recoveryMap = new Map(recoveries.map(r => {
        let images = [];
        if (Array.isArray(r.imageUrls)) {
            images = r.imageUrls;
        } else if (r.imageUrl) {
            images = [r.imageUrl];
        }
        return [r.originalExportId, images];
    }));

    const allOrders = exports.map(order => {
        const customer = customerMap.get(order.customerId);
        const images = recoveryMap.get(order.id) || [];
        return { ...order, customerDetails: customer || {}, images: images };
    });

    const isSuperUser = currentUser?.roleNames?.some(name => name.toLowerCase() === 'admin') || currentUser?.permissions?.viewAllData;

    const visibleOrders = isSuperUser
      ? allOrders
      : allOrders.filter(order =>
          order.createdBy === currentUser.email ||
          order.driverName === currentUser.fullName ||
          order.assistantDriverName === currentUser.fullName
        );
    
    const metrics = {
        total: visibleOrders.length,
        processing: visibleOrders.filter(s => (s.status || 'processing') === 'processing').length,
        shipped: visibleOrders.filter(s => s.status === 'shipped').length,
        cancelled: visibleOrders.filter(s => s.status === 'cancelled').length,
        other: visibleOrders.filter(s => s.status === 'other').length,
    };
        
    const filteredByStatus = statusFilter === 'all' 
        ? visibleOrders 
        : visibleOrders.filter(order => (order.status || 'processing') === statusFilter);

    const finalOrders = filteredByStatus.filter(order => {
        const term = searchTerm.toLowerCase();
        return (
            order.soNumber?.toLowerCase().includes(term) ||
            order.customerName?.toLowerCase().includes(term) ||
            order.customerDetails.phone?.includes(term) ||
            order.customerDetails.address?.toLowerCase().includes(term) ||
            (order.createdBy && order.createdBy.toLowerCase().includes(term))
        );
      });

    return { processedOrders: finalOrders, statusMetrics: metrics };
  }, [exports, customers, recoveries, searchTerm, currentUser, statusFilter]);
  
  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleImageClick = (images, index = 0) => {
    setImageViewer({ open: true, images: images, currentIndex: index });
  };

  const handleCloseImage = () => {
    setImageViewer({ open: false, images: [], currentIndex: 0 });
  };
    
  const handleNextImage = () => {
    setImageViewer(prev => ({ ...prev, currentIndex: (prev.currentIndex + 1) % prev.images.length }));
  };

  const handlePrevImage = () => {
    setImageViewer(prev => ({ ...prev, currentIndex: (prev.currentIndex - 1 + prev.images.length) % prev.images.length }));
  };
  
  if (loading) return <Typography>Đang tải dữ liệu...</Typography>;

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 2 }}>Theo dõi Đơn hàng</Typography>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={2.4}><StatCard title="Tổng số đơn" value={statusMetrics.total} icon={<ReceiptIcon fontSize="large" />} onClick={() => setStatusFilter('all')} selected={statusFilter === 'all'} /></Grid>
        <Grid item xs={12} sm={6} md={2.4}><StatCard title="Đang xử lý" value={statusMetrics.processing} icon={<HourglassTopIcon fontSize="large" />} color="info.main" onClick={() => setStatusFilter('processing')} selected={statusFilter === 'processing'} /></Grid>
        <Grid item xs={12} sm={6} md={2.4}><StatCard title="Đã chuyển" value={statusMetrics.shipped} icon={<LocalShippingIcon fontSize="large" />} color="success.main" onClick={() => setStatusFilter('shipped')} selected={statusFilter === 'shipped'} /></Grid>
        <Grid item xs={12} sm={6} md={2.4}><StatCard title="Đã hủy" value={statusMetrics.cancelled} icon={<CancelIcon fontSize="large" />} color="error.main" onClick={() => setStatusFilter('cancelled')} selected={statusFilter === 'cancelled'} /></Grid>
        <Grid item xs={12} sm={6} md={2.4}><StatCard title="Khác" value={statusMetrics.other} icon={<HelpOutlineIcon fontSize="large" />} color="text.secondary" onClick={() => setStatusFilter('other')} selected={statusFilter === 'other'} /></Grid>
      </Grid>

      <Paper sx={{ p: 2, mb: 2 }}>
        <TextField
          fullWidth
          label="Tìm theo Mã đơn, Khách hàng, Địa chỉ, SĐT, Người tạo..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          size="small"
        />
      </Paper>

      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Mã đơn</TableCell>
                <TableCell>Khách hàng & Liên hệ</TableCell>
                <TableCell>Vận chuyển</TableCell>
                <TableCell>Sản phẩm</TableCell>
                <TableCell>Hình ảnh</TableCell>
                <TableCell>Người tạo</TableCell>
                <TableCell>Trạng thái ĐH</TableCell>
                <TableCell>Trạng thái duyệt</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {processedOrders
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((item) => (
                <TableRow key={item.id} hover>
                  <TableCell sx={{fontWeight: 'bold'}}>{item.soNumber}</TableCell>
                  <TableCell>
                    <Typography variant="body2" component="div">{item.customerName}</Typography>
                    <Typography variant="caption" component="div">{item.customerDetails.address || 'N/A'}</Typography>
                    <Typography variant="caption" component="div">SĐT: {item.customerDetails.phone || 'N/A'}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" component="div">TX: {item.driverName || 'N/A'}</Typography>
                    <Typography variant="caption" component="div">PX: {item.assistantDriverName || 'N/A'}</Typography>
                    <Typography variant="caption" component="div">Xe: {item.vehiclePlate || 'N/A'}</Typography>
                  </TableCell>
                  <TableCell>
                    <Tooltip title={(item.items || []).map(p => p.productName).join(', ')}>
                        <Typography variant="body2" sx={{ display: '-webkit-box', overflow: 'hidden', WebkitBoxOrient: 'vertical', WebkitLineClamp: 2 }}>
                            {(item.items || []).map(p => p.productName).join(', ')}
                        </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    {item.images && item.images.length > 0 ? (
                      <Box sx={{ position: 'relative', display: 'inline-block' }}>
                        <Box
                          component="img"
                          sx={{ height: 50, width: 50, objectFit: 'cover', cursor: 'pointer', borderRadius: 1 }}
                          alt="Thumbnail"
                          src={item.images[0]}
                          onClick={() => handleImageClick(item.images, 0)}
                        />
                        {item.images.length > 1 && (
                            <Chip 
                                label={`+${item.images.length - 1}`} 
                                size="small" 
                                sx={{ position: 'absolute', bottom: 2, right: 2, height: '16px', fontSize: '0.65rem' }} 
                            />
                        )}
                      </Box>
                    ) : ( 'N/A' )}
                  </TableCell>
                  <TableCell>{item.createdBy || 'N/A'}</TableCell>
                  <TableCell>{getOrderStatusChip(item.status)}</TableCell>
                  <TableCell>
                    {item.approvedBy 
                        ? <Chip label="Đã duyệt" color="success" size="small" variant="outlined" /> 
                        : <Chip label="Chưa duyệt" color="default" size="small" variant="outlined" />
                    }
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={processedOrders.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Số dòng mỗi trang:"
        />
      </Paper>

      <Dialog open={imageViewer.open} onClose={handleCloseImage} maxWidth="md" fullWidth>
        <DialogContent sx={{ p: 1, position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <IconButton onClick={handleCloseImage} sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1, color: 'white', bgcolor: 'rgba(0,0,0,0.5)' }}>
                <CloseIcon />
            </IconButton>
            {imageViewer.images.length > 1 && (
                <IconButton onClick={handlePrevImage} sx={{ position: 'absolute', left: 16, zIndex: 1, color: 'white', bgcolor: 'rgba(0,0,0,0.5)' }}>
                    <ArrowBackIosNewIcon />
                </IconButton>
            )}
            <img 
                src={imageViewer.images[imageViewer.currentIndex]} 
                alt={`Recovery ${imageViewer.currentIndex + 1}`} 
                style={{ maxHeight: '80vh', maxWidth: '100%', objectFit: 'contain' }} 
            />
            {imageViewer.images.length > 1 && (
                <IconButton onClick={handleNextImage} sx={{ position: 'absolute', right: 16, zIndex: 1, color: 'white', bgcolor: 'rgba(0,0,0,0.5)' }}>
                    <ArrowForwardIosIcon />
                </IconButton>
            )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}

export default TheoDoiDonHangPage;
